import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { useAnalyticsService } from '@/app/providers/analytics-service/hooks';
import { type ShowPromiseResult } from '@/app/types/adsgram';
import { type ErrorData } from '@/app/types/yandex';

import { useUserActions } from '@/entities/user/model/user.store';
import { AVD_NETWORK } from '@/entities/user/model/user.types';

import { completeAd } from '@/shared/api/api';
import { showPopup } from '@/shared/helpers/show-popup';
import { useObfuscatedAdEvents } from '@/shared/hooks/use-obfuscated-events';
import { adIsFailedEvent } from '@/shared/services/analytics/events/ad-is-failed-event';
import { adIsNotFoundEvent } from '@/shared/services/analytics/events/ad-is-not-found';
import { adIsSkippedEvent } from '@/shared/services/analytics/events/ad-is-skipped-event';
import { adIsStartedEvent } from '@/shared/services/analytics/events/ad-is-started-event';
import { adIsViewedEvent } from '@/shared/services/analytics/events/ad-is-viewed-event';
import { adIsViewedForTicketEvent } from '@/shared/services/analytics/events/ad-is-viewed-for-ticket-event';
import { logErrorMini } from '@/shared/services/service-logger/lib/log-error-mini';

import { CustomErrorCode } from './types';
import { useAdsgram } from './use-ads-gram';
import { useMoneytagAds } from './use-moneytag-ads';
import { type ErrorWithCode, usePlaydeckAdvertisement } from './use-playdeck-ads';
import { useYandexAds } from './use-yandex-ads';

export interface UseAdvertisementProps {
  onReward?: () => void;
  onError?: (error: Error) => void;

  onSkip?: () => void;

  eventName?: string;
  taskId?: number;
  ymid?: string | null;
  prizeSaleId?: number;

  /**
   *
   */
  promisesToRefetch?: (() => Promise<unknown>)[];
  withPriority?: boolean;
  viewAdForTicket?: boolean;
}

export const useAdvertisement = (
  source: string,
  {
    eventName,
    taskId,
    ymid,
    prizeSaleId,

    onReward,
    onError,

    onSkip,
    viewAdForTicket,

    promisesToRefetch = [],
    withPriority = false,
  }: UseAdvertisementProps = {}
) => {
  const { t } = useTranslation();

  const analyticsService = useAnalyticsService();

  const { fetchUser } = useUserActions();

  const { sendObfuscatedEvents } = useObfuscatedAdEvents();

  const makeHandleReward = useCallback(
    (avdNetwork: AVD_NETWORK) => async () => {
      try {
        const viewedEventProps: Record<string, any> = {
          avd_network: avdNetwork,
          view_source: source,
        };

        if (eventName) {
          viewedEventProps['name'] = eventName;
        }

        if (prizeSaleId) {
          viewedEventProps['prize_sale_id'] = prizeSaleId;
        }

        const e = viewAdForTicket
          ? adIsViewedForTicketEvent({ event_properties: viewedEventProps })
          : adIsViewedEvent({ event_properties: viewedEventProps });

        analyticsService.send(e);

        sendObfuscatedEvents();

        if (taskId !== undefined) await completeAd(taskId);

        void Promise.all([fetchUser(), ...promisesToRefetch.map((promise) => promise())]);

        onReward?.();
      } catch (err) {
        showPopup({ message: (err as any).toString() });
      }
    },
    [
      analyticsService,
      eventName,
      fetchUser,
      onReward,
      prizeSaleId,
      promisesToRefetch,
      sendObfuscatedEvents,
      source,
      taskId,
      viewAdForTicket,
    ]
  );

  const makeHandleError = useCallback(
    (avdNetwork: AVD_NETWORK) =>
      (error: Error | ErrorData | ErrorWithCode | ShowPromiseResult) => {
        let message = t('Something went wrong');

        if ('text' in error) message = error.text;
        if ('message' in error) message = error.message;
        if ('description' in error) message = error.description;

        if ('code' in error && error.code === CustomErrorCode.NO_AVAILABLE_ADS) {
          const e = adIsNotFoundEvent({
            event_properties: {
              avd_network: avdNetwork,
              view_source: source,
              error: { message },
            },
          });

          analyticsService.send(e);
        } else {
          const e = adIsFailedEvent({
            event_properties: {
              avd_network: avdNetwork,
              view_source: source,
              error: { message },
            },
          });

          analyticsService.send(e);
        }

        if (!withPriority) showPopup({ message });

        onError?.(new Error(message));
      },
    [analyticsService, onError, source, t, withPriority]
  );

  const makeHandleSkip = useCallback(
    (avdNetwork: AVD_NETWORK) => () => {
      const e = adIsSkippedEvent({
        event_properties: {
          avd_network: avdNetwork,
          view_source: source,
        },
      });

      analyticsService.send(e);

      onSkip?.();
    },
    [analyticsService, onSkip, source]
  );

  const showAdsGramAds = useAdsgram({
    onReward: makeHandleReward(AVD_NETWORK.AG),
    onError: makeHandleError(AVD_NETWORK.AG),
    onSkip: makeHandleSkip(AVD_NETWORK.AG),
  });

  const { open: showPlaydeckAds } = usePlaydeckAdvertisement(source, {
    onReward: makeHandleReward(AVD_NETWORK.PD),
    onError: makeHandleError(AVD_NETWORK.PD),

    taskId,
  });

  const showYandexAds = useYandexAds({
    onReward: makeHandleReward(AVD_NETWORK.Y),
    onError: makeHandleError(AVD_NETWORK.Y),
    onSkip: makeHandleSkip(AVD_NETWORK.Y),
  });

  const showMoneytagAds = useMoneytagAds({
    ymid: ymid,
    onReward: makeHandleReward(AVD_NETWORK.MT),
    onError: makeHandleError(AVD_NETWORK.MT),
    // The onSkip method is not built into the Moneytag SDK
  });

  const show = useCallback(
    (avdNetwork: AVD_NETWORK) => {
      const e = adIsStartedEvent({
        event_properties: {
          avd_network: avdNetwork,
          view_source: source,
        },
      });

      analyticsService.send(e);

      switch (avdNetwork) {
        case AVD_NETWORK.AG:
          void showAdsGramAds();
          break;

        case AVD_NETWORK.PD:
          showPlaydeckAds();
          break;

        case AVD_NETWORK.Y:
          void showYandexAds();
          break;

        case AVD_NETWORK.MT:
          void showMoneytagAds();
          break;

        default:
          void logErrorMini('Unsupported type of advertisement', 'use-advertisement.ts', {
            avdNetwork,
          });

          showPopup({
            message: `Unsupported type of advertisement: ${avdNetwork}`,
          });
          break;
      }
    },
    [
      analyticsService,
      showAdsGramAds,
      showPlaydeckAds,
      showYandexAds,
      showMoneytagAds,
      source,
    ]
  );

  return show;
};
